import React, { useState, useEffect, useCallback } from 'react';
import { leaveCancellationAPI } from '../../api/leaveCancellationAPI';
import { usersAPI } from '../../api/usersAPI';
import { attendanceAPI } from '../../api/attendanceAPI';
import './leaveCancellation.css';
import UserPcode from '../../components/userPcode/userPcode';
import EmployeeSelector from '../../components/employeeSelector/employeeSelector';
import useUserName from '../../common/hooks/useUserName';

const LeaveCancellation = () => {
    const { userName } = useUserName();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedCenter, setSelectedCenter] = useState('');
    const [centers, setCenters] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [employees, setEmployees] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showOnlyRequested, setShowOnlyRequested] = useState(false);
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [cancellationReason, setCancellationReason] = useState('');
    const [selectedPcode, setSelectedPcode] = useState('');
    const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
    const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [authorizationLevels, setAuthorizationLevels] = useState([]);
    const [loadingAuthLevels, setLoadingAuthLevels] = useState(false);



    useEffect(() => {
        initializePage();
    }, []);

    const initializePage = async () => {
        try {
            const pcode = localStorage.getItem('PCode');
            const empCode = localStorage.getItem('EmpCode');

            // Set default dates (current month)
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            setFromDate(firstDay.toISOString().split('T')[0]);
            setToDate(lastDay.toISOString().split('T')[0]);
            setSelectedCenter(pcode);

            // Load centers
            const centersData = await usersAPI.getPCentersUserWise(empCode);
            setCenters(centersData);

            // Check if user is admin
            const adminResult = await attendanceAPI.checkAdminAccess(pcode);
            setIsAdmin(adminResult.isAdmin);

            if (adminResult.isAdmin) {
                // Load employee list for admin
                const employeesData = await usersAPI.getEmployeesByPcode(pcode);
                setEmployees(employeesData);
            } else {
                setSelectedEmployee(empCode);
            }
        } catch (error) {
            console.error('Error initializing page:', error);
            showMessage('Error initializing page', 'error');
        }
    };

    const onChangePCode = (pcode) => {
        setSelectedPcode(pcode);
        setSelectedEmployee(null);
        setLoggedInEmployee(null);
    };

    const handlePreview = async () => {
        if (!fromDate || !toDate) {
            showMessage('Please select both from and to dates', 'error');
            return;
        }

        if (new Date(fromDate) > new Date(toDate)) {
            showMessage('From date cannot be later than to date', 'error');
            return;
        }

        const empCode = isAdmin ? selectedEmployee : localStorage.getItem('EmpCode');
        if (isAdmin && !empCode) {
            showMessage('Please select an employee', 'error');
            return;
        }

        setLoading(true);
        try {
            const leavesData = await leaveCancellationAPI.getCancellableLeaves(
                fromDate,
                toDate,
                selectedPcode,
                empCode,
                showOnlyRequested
            );

            const filteredLeaves = showOnlyRequested
                ? leavesData
                : leavesData.filter(leave => {
                    if (leave.isRequestedCancel &&
                        (leave.authorizationStatus === 'NotStarted' ||
                            leave.authorizationStatus === 'Partial' ||
                            leave.status === 'Pending')) {
                        return false;
                    }
                    return true;
                });

            setLeaves(filteredLeaves);

            if (filteredLeaves.length === 0) {
                showMessage('No leaves found for the selected criteria', 'info');
            }
        } catch (error) {
            console.error('Error fetching leaves:', error);
            showMessage('Error fetching leaves', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRequest = async (leave) => {
        setSelectedLeave(leave);
        setCancellationReason('');
        setShowCancelModal(true);

        // Load authorization levels for this leave
        setLoadingAuthLevels(true);
        try {
            const levels = await leaveCancellationAPI.getAuthorizationLevels(leave.leaveId, selectedPcode);
            setAuthorizationLevels(levels);
        } catch (error) {
            console.error('Error loading authorization levels:', error);
            setAuthorizationLevels([]);
        } finally {
            setLoadingAuthLevels(false);
        }
    };

    const submitCancellation = async () => {
        if (!cancellationReason.trim()) {
            showMessage('Please provide a cancellation reason', 'error');
            return;
        }

        try {
            setLoading(true);
            await leaveCancellationAPI.submitLeaveCancellation(
                selectedPcode,
                selectedLeave.leaveId,
                cancellationReason,
                selectedLeave.empCode
            );

            showMessage('Leave cancellation request submitted successfully', 'success');

            // Remove the cancelled leave from the UI if it's not yet approved/rejected
            // or if it's only partially approved (meaning it can be completely cancelled)
            if (selectedLeave.authorizationStatus === 'NotStarted' ||
                selectedLeave.authorizationStatus === 'Partial' ||
                selectedLeave.status === 'Pending') {
                setLeaves(prevLeaves =>
                    prevLeaves.filter(leave => leave.leaveId !== selectedLeave.leaveId)
                );
            } else {
                // For fully approved leaves, refresh the list to show updated status
                handlePreview();
            }

            setShowCancelModal(false);
            setAuthorizationLevels([]);
        } catch (error) {
            console.error('Error submitting cancellation:', error);
            showMessage('Error submitting cancellation request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (text, type) => {
        setMessage(text);
        setMessageType(type);
        setTimeout(() => {
            setMessage('');
            setMessageType('');
        }, 5000);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusBadgeClass = (status) => {
        if (!status) return 'badge bg-secondary';

        switch (status.toLowerCase()) {
            case 'pending': return 'badge bg-warning text-dark';
            case 'approved': return 'badge bg-success';
            case 'rejected': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    };
    const checkAdminAccess = useCallback(async () => {
        setIsCheckingAdmin(true);
        try {
            const result = await attendanceAPI.checkAdminAccess(selectedPcode);
            setIsAdmin(result.isAdmin);
        } catch (error) {
            console.error('Error checking admin access:', error);
            setIsAdmin(false);
        } finally {
            setIsCheckingAdmin(false);
        }
    }, [selectedPcode]);

    const loadLoggedInEmployee = useCallback(async () => {
        if (!selectedPcode || selectedPcode.trim() === '') return;

        setIsLoadingEmployee(true);
        try {
            const employees = await attendanceAPI.getEmployees(selectedPcode);
            const currentEmployee = employees.find(emp => emp.employeeCode === userName);
            if (currentEmployee) {
                setLoggedInEmployee(currentEmployee);
            }
        } catch (error) {
            console.error('Error loading logged in employee:', error);
        } finally {
            setIsLoadingEmployee(false);
        }
    }, [selectedPcode, userName]);

    // Check admin access when pcode changes
    useEffect(() => {
        if (selectedPcode && selectedPcode.trim() !== '') {
            checkAdminAccess();
            loadLoggedInEmployee();
        }
    }, [selectedPcode, checkAdminAccess, loadLoggedInEmployee]);

    // Set logged-in employee as selected when user is not admin
    useEffect(() => {
        if (!isAdmin && loggedInEmployee && !isCheckingAdmin) {
            setSelectedEmployee(loggedInEmployee);
        }
    }, [isAdmin, loggedInEmployee, isCheckingAdmin]);

    const onChangeEmployee = (employee) => {
        setSelectedEmployee(employee.employeeCode);
    }

    return (
        <>
            <div className="row">
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12 p-2 rounded contentLeveApproval form__body">
                    <div className="row mt-1">
                        <div className="col-2 col-sm-2 col-md-4 col-lg-4 col-xl-4 col-xxl-4 text-start"></div>
                        <div className="col-8 col-sm-8 col-md-4 col-lg-4 col-xl-4 col-xxl-4 text-center">
                            <h4>Leave Cancellation</h4>
                        </div>
                        <div className="col-2 col-sm-2 col-md-4 col-lg-4 col-xl-4 col-xxl-4 text-center"></div>
                    </div>

                    <div className="row">
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                        <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                            <div className="input-group input-group-sm">
                                <UserPcode setSelectedPcode={setSelectedPcode} triggerOnChangePCode={onChangePCode} />
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    <div className="row">
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                        <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                            <div className="input-group input-group-sm">
                                <EmployeeSelector
                                    selectedPcode={selectedPcode}
                                    setSelectedEmployee={setSelectedEmployee}
                                    disabled={!isAdmin || isCheckingAdmin || isLoadingEmployee}
                                    placeholder={
                                        isCheckingAdmin || isLoadingEmployee ? "Loading..." :
                                            !isAdmin ? loggedInEmployee ? `(${loggedInEmployee.employeeCode}) ${loggedInEmployee.employeeName}` : "Loading your details..." :
                                                "Select Employee"
                                    }
                                    triggerOnChangeEmployee={onChangeEmployee}
                                    selectedEmployee={selectedEmployee}
                                />
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    <div className="row">
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3 col-xxl-3 mb-2 mb-sm-0">
                            <div className="input-group input-group-sm">
                                <input
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    type="date"
                                    className="form-control"
                                    placeholder="From Date"
                                />
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3 col-xxl-3">
                            <div className="input-group input-group-sm">
                                <input
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    type="date"
                                    className="form-control"
                                    placeholder="To Date"
                                />
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    <div className="row">
                        <div className="col-12 col-sm-12 col-md-9 col-lg-9 col-xl-9 col-xxl-9 d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showOnlyRequested"
                                    checked={showOnlyRequested}
                                    onChange={(e) => {
                                        setShowOnlyRequested(e.target.checked);
                                        // Ensure data is only refreshed on Preview
                                        setLeaves([]);
                                        showMessage('Click Preview to refresh results', 'info');
                                    }}
                                />
                                <label className="form-check-label text-muted mr-2" htmlFor="showOnlyRequested">
                                    Show only requested cancels
                                </label>
                            </div>
                            <div className="btn-group btn-group-sm mb-2 mb-md-0 me-md-3">
                                <button onClick={handlePreview} type="button" className="btn btn-success" disabled={loading}>
                                    {loading ? 'Loading...' : 'Preview'}
                                </button>
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    {/* Messages */}
                    {message && (
                        <div className="row">
                            <div className="col-12">
                                <div className={`alert alert-${messageType === 'error' ? 'danger' : messageType === 'success' ? 'success' : 'info'} alert-dismissible fade show`} role="alert">
                                    {message}
                                    <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Leave Cards */}
                    <div className="row">
                        <div className="col-12 table-responsive">
                            {leaves.length > 0 ? (
                                <div className="row">
                                    {leaves.map((leave, index) => (
                                        <div key={index} className="col-lg-6 col-12 mb-3">
                                            <div className={`card leave-card ${leave.isRequestedCancel ? 'requested-cancel' :
                                                leave.authorizationStatus === 'Partial' ? 'partial-auth' :
                                                    !leave.canCancel ? 'unauthorized' : ''
                                                }`}>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="card-title mb-0">{leave.leaveType}</h6>
                                                        {showOnlyRequested ? (
                                                            // In requested-cancels view, show Cancelled/Pending Cancel status clearly based on data
                                                            <span className={`badge ${leave.isCancel ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                                {leave.isCancel ? 'Cancelled' : 'Cancellation Pending'}
                                                            </span>
                                                        ) : (
                                                            <span className={getStatusBadgeClass(leave.status)}>
                                                                {leave.status}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {isAdmin && (
                                                        <p className="text-muted mb-2">
                                                            <small><strong>Employee:</strong> {leave.empCode} - {leave.empName} </small>
                                                        </p>
                                                    )}

                                                    <div className="leave-details">
                                                        <div className="row">
                                                            <div className="col-6">
                                                                <small className="text-muted">From Date</small>
                                                                <div>{formatDate(leave.fromDate)}</div>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="text-muted">To Date</small>
                                                                <div>{formatDate(leave.toDate)}</div>
                                                            </div>
                                                        </div>

                                                        <div className="row">
                                                            <div className="col-6">
                                                                <small className="text-muted">No of Days</small>
                                                                <div>{leave.days}</div>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="text-muted">Applied Date</small>
                                                                <div>{formatDate(leave.appliedDate)}</div>
                                                            </div>
                                                        </div>

                                                        {leave.reason && (
                                                            <div className="mt-2">
                                                                <small className="text-muted">Reason</small>
                                                                <div className="reason-text">{leave.reason}</div>
                                                            </div>
                                                        )}

                                                        {/* Hide authorization labels in requested-cancels view to reduce confusion */}
                                                        {!showOnlyRequested && leave.authorizationStatus && (
                                                            <div className="mt-2">
                                                                <small className="text-muted">Authorization Status</small>
                                                                <div className="d-flex align-items-center">
                                                                    <span className={`badge me-2 ${leave.authorizationStatus === 'NotStarted' ? 'bg-secondary' :
                                                                        leave.authorizationStatus === 'Partial' ? 'bg-warning text-dark' :
                                                                            leave.authorizationStatus === 'Complete' ? 'bg-success' : 'bg-info'
                                                                        }`}>
                                                                        {leave.authorizationStatus === 'NotStarted' ? 'Pending Authorization' :
                                                                            leave.authorizationStatus === 'Partial' ? 'Partially Authorized' :
                                                                                leave.authorizationStatus === 'Complete' ? 'Fully Processed' : leave.authorizationStatus}
                                                                    </span>
                                                                    {leave.authorizationStatus === 'Partial' && !leave.canCancel && (
                                                                        <small className="text-warning">
                                                                            <i className="fas fa-exclamation-triangle me-1"></i>
                                                                            Only management can cancel
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {leave.isRequestedCancel && (
                                                            <div className="mt-2">
                                                                <div className="alert alert-info mb-0 py-2">
                                                                    <small>
                                                                        <i className="fas fa-info-circle me-1"></i>
                                                                        Cancellation requested on {formatDate(leave.cancelRequestDate)}
                                                                        {leave.cancellationReason && (
                                                                            <>
                                                                                <br />
                                                                                <span className="ms-3">Reason: {leave.cancellationReason}</span>
                                                                            </>
                                                                        )}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {leave.canCancel && !leave.isRequestedCancel && (
                                                        <div className="mt-3">
                                                            <button
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleCancelRequest(leave)}
                                                            >
                                                                <i className="fas fa-times me-1"></i>
                                                                Cancel Leave
                                                            </button>
                                                        </div>
                                                    )}

                                                    {!leave.canCancel && !leave.isRequestedCancel && (
                                                        <div className="mt-3">
                                                            <small className="text-muted">
                                                                <i className="fas fa-info-circle me-1"></i>
                                                                {leave.status === 'Rejected' ?
                                                                    'Rejected leaves cannot be cancelled' :
                                                                    leave.authorizationStatus === 'Partial' || leave.authorizationStatus === 'Complete' ?
                                                                        'Leave has approval activity - only management can cancel' :
                                                                        'You are not authorized to cancel this leave'
                                                                }
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                !loading && (
                                    <div className="text-center py-5">
                                        <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                                        <h6 className="text-muted">No leaves found</h6>
                                        <p className="text-muted">Try adjusting your search criteria</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Cancellation Modal */}
            {showCancelModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Request Leave Cancellation</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowCancelModal(false);
                                        setAuthorizationLevels([]);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {selectedLeave && (
                                    <div className="mb-3">
                                        <h6>{selectedLeave.leaveType}</h6>
                                        <p className="text-muted">
                                            {formatDate(selectedLeave.fromDate)} to {formatDate(selectedLeave.toDate)}
                                            ({selectedLeave.days} days)
                                        </p>

                                        {/* Authorization Levels Display */}
                                        {loadingAuthLevels ? (
                                            <div className="text-center">
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading authorization levels...</span>
                                                </div>
                                                <small className="text-muted ms-2">Loading authorization levels...</small>
                                            </div>
                                        ) : authorizationLevels.length > 0 && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block mb-2">Authorization Levels:</small>
                                                <div className="auth-levels-container">
                                                    {authorizationLevels.map((level, index) => (
                                                        <div key={index} className="auth-level-item d-flex justify-content-between align-items-center py-2 px-3 mb-2 rounded"
                                                            style={{
                                                                backgroundColor: level.Status === 'Approved' ? '#d4edda' :
                                                                    level.Status === 'Rejected' ? '#f8d7da' :
                                                                        level.Status === 'Pending' ? '#fff3cd' : '#f8f9fa'
                                                            }}>
                                                            <div>
                                                                <small className="fw-bold">{level.LevelCode}</small>
                                                                <br />
                                                                <small>{level.EmpName} ({level.EmpCode})</small>
                                                            </div>
                                                            <span className={`badge ${level.Status === 'Approved' ? 'bg-success' :
                                                                level.Status === 'Rejected' ? 'bg-danger' :
                                                                    level.Status === 'Pending' ? 'bg-warning text-dark' : 'bg-secondary'
                                                                }`}>
                                                                {level.Status}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="alert alert-info mt-2">
                                                    <small>
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        Cancellation request will be processed through the authorization workflow. For partially approved leaves, the next pending approver will review the cancellation.
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label">Cancellation Reason *</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={cancellationReason}
                                        onChange={(e) => setCancellationReason(e.target.value)}
                                        placeholder="Please provide reason for cancellation..."
                                        required
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowCancelModal(false);
                                        setAuthorizationLevels([]);
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={submitCancellation}
                                    disabled={loading || !cancellationReason.trim()}
                                >
                                    {loading ? 'Submitting...' : 'Submit Cancellation'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LeaveCancellation;
